import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getEntry, getScores, saveCard, submitCard } from "../lib/api";
import { shotsReceived, points as pointsFor } from "../lib/scoring";
import { Notice, Loading } from "../components/ui";
import Back from "../components/Back";
import Scorecard from "../components/Scorecard";

/**
 * Entering a card.
 *
 * On a phone: one hole, filling the screen, with buttons sized around
 * that hole's par. No keyboard — it would eat half the screen — and
 * nothing advances on its own, because people misread paper cards and
 * need to go back.
 *
 * On a wide screen: the whole card as a grid, which is what an organiser
 * typing up five cards at the bar actually wants.
 *
 * Everything else that used to be on this screen — the explanation of no
 * returns, the legend, the running totals in three places — has gone or
 * moved. A person entering a card wants the hole they're on and the
 * number they're about to type.
 */

function useWide() {
  const [wide, setWide] = useState(
    typeof window !== "undefined" && window.innerWidth >= 820);
  useEffect(() => {
    const on = () => setWide(window.innerWidth >= 820);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return wide;
}

export default function ScoreEntry() {
  const { entryId } = useParams();
  const nav = useNavigate();
  const wide = useWide();

  const [entry, setEntry] = useState(null);
  const [scores, setScores] = useState({});
  const [current, setCurrent] = useState(1);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [done, setDone] = useState(false);
  const [review, setReview] = useState(false);

  useEffect(() => {
    Promise.all([getEntry(entryId), getScores(entryId)])
      .then(([e, s]) => {
        setEntry(e);
        const map = {};
        s.forEach((r) => { map[r.hole_number] = r.strokes; });
        setScores(map);
        // start on the first hole without a score, not back at the 1st
        const holes = [...(e?.tee_sets?.tee_holes ?? [])]
          .sort((a, b) => a.hole_number - b.hole_number);
        const next = holes.find((h) => map[h.hole_number] === undefined);
        setCurrent(next?.hole_number ?? 1);
      })
      .catch((err) => setError(err.message));
  }, [entryId]);

  const holes = useMemo(() => {
    const raw = entry?.tee_sets?.tee_holes ?? [];
    return [...raw].sort((a, b) => a.hole_number - b.hole_number);
  }, [entry]);

  const ph = entry?.playing_handicap ?? 0;

  const totals = useMemo(() => {
    let gross = 0, pts = 0, entered = 0, notHoled = 0;
    holes.forEach((h) => {
      const v = scores[h.hole_number];
      const sh = shotsReceived(ph, h.stroke_index);
      if (v !== undefined) entered++;
      if (v === null) notHoled++;
      pts += pointsFor(v ?? null, h.par, sh);
      if (v != null) gross += v;
    });
    return { gross, pts, entered, notHoled };
  }, [scores, holes, ph]);

  const set = (n, v) => { setScores((p) => ({ ...p, [n]: v })); setDirty(true); };

  async function save(thenSubmit = false) {
    setError(""); setSaving(true);
    try {
      await saveCard(entryId,
        holes.filter((h) => scores[h.hole_number] !== undefined)
             .map((h) => ({ hole_number: h.hole_number, strokes: scores[h.hole_number] })));
      setDirty(false);
      if (thenSubmit) { await submitCard(entryId); setDone(true); }
    } catch (err) {
      setError(err.message ?? String(err));
    } finally { setSaving(false); }
  }

  if (error && !entry) return <Notice kind="error">{error}</Notice>;
  if (!entry) return <Loading />;
  if (!entry) return (
    <>
      <Back to="/">Back</Back>
      <Notice kind="error">That card isn't available.</Notice>
    </>
  );

  const round = entry.rounds;
  const noReturn = round?.competitions?.requires_hole_out && totals.notHoled > 0;
  const complete = totals.entered === holes.length;

  /* ---------- submitted ---------- */
  if (done) {
    return (
      <>
        <Back to={round?.competition_id ? `/c/${round.competition_id}/r/${round.id}` : '/'}>{round?.name ?? "Round"}</Back>
        <div className="done-card">
          <span className="done-tick" aria-hidden="true" />
          <h1>Card submitted</h1>
          <p>{entry.profiles?.full_name} · {round?.name}</p>
          {noReturn ? (
            <div className="done-figs">
              <div><span>Gross</span><b>{totals.gross}</b></div>
              <div><span>Nett</span><b style={{ color: "var(--red)" }}>NR</b></div>
              <div><span>Points</span><b>{totals.pts}</b></div>
            </div>
          ) : (
            <div className="done-figs">
              <div><span>Gross</span><b>{totals.gross}</b></div>
              <div><span>Nett</span><b>{totals.gross - ph}</b></div>
              <div><span>Points</span><b>{totals.pts}</b></div>
            </div>
          )}
          {noReturn && (
            <p className="done-note">
              {totals.notHoled} hole{totals.notHoled === 1 ? "" : "s"} not holed
              out, so this posts no nett score. Your points still count.
            </p>
          )}
          <button className="btn btn-primary btn-block"
                  onClick={() => nav(`/c/${round.competition_id}/r/${round.id}`)}>
            Back to the round
          </button>
        </div>
      </>
    );
  }

  /* ---------- who and where, in one quiet line ---------- */
  const context = (
    <div className="entry-top">
      <Back to={round?.competition_id ? `/c/${round.competition_id}/r/${round.id}` : '/'}>{round?.name ?? "Round"}</Back>
      <div className="entry-who">
        <strong>{entry.profiles?.full_name}</strong>
        <small>
          {round?.courses?.name} · {entry.tee_sets?.name} tees · playing off {ph}
        </small>
      </div>
    </div>
  );

  /* ---------- the bar that's always there ---------- */
  const footer = (
    <div className="entry-foot">
      <div className="entry-figs">
        <span><small>Gross</small><b>{totals.gross || "–"}</b></span>
        <span><small>Nett</small>
          <b style={{ color: noReturn ? "var(--red)" : undefined }}>
            {noReturn ? "NR" : totals.gross ? totals.gross - ph : "–"}
          </b></span>
        <span><small>Points</small><b>{totals.pts || "–"}</b></span>
        <span><small>Recorded</small><b>{totals.entered}/{holes.length}</b></span>
      </div>
      <div className="entry-actions">
        <button className="btn btn-ghost" onClick={() => save(false)}
                disabled={saving || !dirty}>
          {saving ? "Saving…" : dirty ? "Save" : "Saved"}
        </button>
        <button className="btn btn-primary"
                onClick={() => (wide ? save(true) : setReview(true))}
                disabled={saving || !complete}>
          {complete ? (wide ? (noReturn ? "Submit as no return" : "Submit card")
                            : "Check the card")
                    : `${holes.length - totals.entered} to go`}
        </button>
      </div>
    </div>
  );

  /* ---------- wide: the whole card ---------- */
  if (wide) {
    return (
      <>
        {context}
        <Notice kind="error">{error}</Notice>
        <Scorecard holes={holes} scores={scores} playingHandicap={ph}
                   onChange={(n, v) => set(n, v)} />
        <p className="muted" style={{ fontSize: ".75rem", marginTop: ".6rem" }}>
          Tab between holes. Type <strong>NR</strong> for a hole that wasn't
          holed out.
        </p>
        {footer}
      </>
    );
  }

  /* ---------- check it before signing ---------- */
  if (review && !wide) {
    return (
      <>
        <Back to={round?.competition_id ? `/c/${round.competition_id}/r/${round.id}` : '/'}>
          {round?.name ?? "Round"}
        </Back>

        <h1 style={{ fontSize: "1.4rem", marginBottom: ".2rem" }}>Check your card</h1>
        <p className="muted" style={{ fontSize: ".875rem", lineHeight: 1.6, margin: "0 0 1rem" }}>
          {entry.profiles?.full_name} · {round?.courses?.name} · playing off {ph}.
          Tap any hole to change it.
        </p>

        <Notice kind="error">{error}</Notice>

        <div className="check">
          {[[0, 9, "Out"], [9, 18, "In"]].map(([from, to, label]) => (
            <div className="check-nine" key={label}>
              <div className="check-head">
                <span>{label}</span>
                <span>{holes.slice(from, to)
                  .reduce((a, x) => a + (scores[x.hole_number] ?? 0), 0)}</span>
              </div>
              {holes.slice(from, to).map((x) => {
                const sc = scores[x.hole_number];
                const d = sc == null ? null : sc - x.par;
                const cls = d === null ? "" : d <= -2 ? " eagle" : d === -1 ? " birdie"
                          : d === 0 ? "" : d === 1 ? " bogey" : " double";
                return (
                  <button className="check-row" key={x.hole_number}
                          onClick={() => { setCurrent(x.hole_number); setReview(false); }}>
                    <span className="check-hole">{x.hole_number}</span>
                    <span className="check-par">par {x.par}</span>
                    <span className={`check-score${cls}`}>
                      {sc === null ? "NR" : sc}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="check-totals">
          <div><span>Gross</span><b>{totals.gross}</b></div>
          <div><span>Nett</span>
            <b style={{ color: noReturn ? "var(--red)" : undefined }}>
              {noReturn ? "NR" : totals.gross - ph}
            </b></div>
          <div><span>Points</span><b>{totals.pts}</b></div>
        </div>

        {noReturn && (
          <div className="notice notice-info" style={{ marginBottom: "1rem" }}>
            {totals.notHoled} hole{totals.notHoled === 1 ? "" : "s"} picked up, so
            this posts no nett score. Your points still count.
          </div>
        )}

        <div className="row" style={{ gap: ".5rem" }}>
          <button className="btn btn-ghost" onClick={() => setReview(false)}>
            Keep editing
          </button>
          <button className="btn btn-primary" style={{ flex: 1 }}
                  onClick={() => save(true)} disabled={saving}>
            {saving ? "Submitting…" : noReturn ? "Sign and submit as NR" : "Sign and submit"}
          </button>
        </div>
      </>
    );
  }

  /* ---------- phone: one hole, one number ---------- */
  const h = holes.find((x) => x.hole_number === current) ?? holes[0];
  if (!h) return <Notice kind="error">This tee has no holes recorded.</Notice>;

  const shots = shotsReceived(ph, h.stroke_index);
  const v = scores[h.hole_number];
  const go = (n) => setCurrent(Math.min(holes.length, Math.max(1, n)));

  /**
   * A stepper rather than a row of buttons.
   *
   * Unset, it shows the hole's par in grey — most scores are within a
   * shot or two of it, so that's the fewest taps from the truth. The
   * first press on either arrow commits that starting point and moves
   * from it, so nothing is recorded by accident.
   */
  /**
   * The number starts at par and scrolls either way. Nothing is recorded
   * until the player touches it or presses Next — so a card left half
   * done doesn't quietly fill with pars, but a player who made par taps
   * once and moves on.
   */
  const shown = v === undefined ? h.par : v;
  const step = (by) => {
    if (v === null) { set(h.hole_number, h.par); return; }   // out of picked-up
    set(h.hole_number, Math.max(1, Math.min(20, shown + by)));
  };
  const nextHole = () => {
    if (v === undefined) set(h.hole_number, h.par);          // Next confirms par
    go(current + 1);
  };

  const toPar = shown - h.par;
  const shownSays =
    toPar <= -2 ? "eagle or better"
    : toPar === -1 ? "birdie"
    : toPar === 0 ? "par"
    : toPar === 1 ? "bogey"
    : `${toPar} over`;

  return (
    <>
      {context}
      <Notice kind="error">{error}</Notice>

      <div className="hole">
        <div className="hole-head">
          <button className="hole-step" onClick={() => go(current - 1)}
                  disabled={current === 1} aria-label="Previous hole">‹</button>
          <div className="hole-num">
            <b>Hole {h.hole_number}</b>
            <small>
              Par {h.par} · SI {h.stroke_index}
              {shots ? ` · ${shots} shot${shots > 1 ? "s" : ""}` : " · no shot"}
            </small>
          </div>
          <button className="hole-step" onClick={() => go(current + 1)}
                  disabled={current === holes.length} aria-label="Next hole">›</button>
        </div>

        <div className="stepper">
          <button className="stepper-btn" onClick={() => step(-1)}
                  disabled={shown <= 1} aria-label="One fewer">−</button>

          <div className="stepper-val">
            {v === null ? (
              <><b className="nr">NR</b><small>picked up</small></>
            ) : (
              <>
                <b>{shown}</b>
                <small>
                  {shownSays} · {pointsFor(shown, h.par, shots)} point
                  {pointsFor(shown, h.par, shots) === 1 ? "" : "s"}
                </small>
              </>
            )}
          </div>

          <button className="stepper-btn" onClick={() => step(1)}
                  aria-label="One more">+</button>
        </div>

        <div className="hole-extra">
          <button className={`pad wide${v === null ? " on" : ""}`}
                  onClick={() => set(h.hole_number, v === null ? undefined : null)}>
            {v === null ? "Not picked up" : "Picked up"}
          </button>
          <button className="pad wide ghost" disabled={v === undefined}
                  onClick={() => set(h.hole_number, undefined)}>
            Clear
          </button>
        </div>

        <button className="btn btn-primary btn-block next-hole"
                onClick={() => (current === holes.length ? setReview(true) : nextHole())}>
          {current === holes.length ? "Check the card" : "Next hole"}
        </button>
      </div>

      {/* the card so far: the score itself, marked as it would be on
          paper, rather than a row of blue dots saying "something here" */}
      <div className="mini">
        {[[0, 9, "Out"], [9, 18, "In"]].map(([from, to, label]) => (
          <div className="mini-row" key={label}>
            <span className="mini-label">{label}</span>
            {holes.slice(from, to).map((x) => {
              const sc = scores[x.hole_number];   // undefined = not recorded yet
              const d = sc == null ? null : sc - x.par;
              const mark = d === null ? "" : d <= -2 ? " eagle" : d === -1 ? " birdie"
                        : d === 0 ? " level" : d === 1 ? " bogey" : " double";
              return (
                <button key={x.hole_number}
                        className={`mini-cell${mark}${sc === null ? " nr" : ""}${x.hole_number === current ? " now" : ""}`}
                        onClick={() => go(x.hole_number)}
                        aria-label={`Hole ${x.hole_number}`}>
                  {sc === null ? "–" : sc ?? x.hole_number}
                </button>
              );
            })}
            <span className="mini-sum">
              {holes.slice(from, to).reduce((a, x) => a + (scores[x.hole_number] ?? 0), 0) || "–"}
            </span>
          </div>
        ))}
      </div>

      {footer}
    </>
  );
}
